// Main Platformer Game Component for Helix 2000
// MapleStory-inspired 2D side-scrolling MMORPG

import React, { Profiler, useEffect, useMemo, useRef, useState } from 'react';
import { DevConfig } from '../config/environment/devConfig';

import { gameLogger } from '../utils/GameLogger';

import { useDevActions } from './hooks/useDevActions';
import { installHelixDebugConsole } from './debug/helixDebugConsole';
import { useServerConnection } from './hooks/useServerConnection';
import { useGameEvents } from './hooks/useGameEvents';
import { useXPFeedback } from './hooks/useXPFeedback';
import { useEditorMode } from './hooks/useEditorMode';
import { useDebugConfig } from './hooks/useDebugConfig';
import { ActionDispatcher } from './services/ActionDispatcher';
import { uiActionBridge } from './ui/framework/uiActionBridge';

import { CharacterService } from './services/CharacterService';
import { BootService } from './services/BootService';
import { ChatCommandProcessor } from './services/ChatCommandProcessor';
import { ErrorBoundary } from './ui/components/ErrorBoundary';
import { ErrorToastOverlay } from './ui/components/ErrorToast';
import { CombatLogPanel } from './ui/panels/CombatLogPanel';
import { ScreensaverOverlay } from './ui/overlays/ScreensaverOverlay';
import { ScreensaverController } from './ui/overlays/ScreensaverController';
import { isScreensaverMode } from '../config/screensaver/isScreensaverMode';


import { GameUIOverlay } from './ui/GameUIOverlay';
import { useGame } from './ui/contexts/GameContext';
import { ControllerNavigationProvider } from './ui/input/controller/ControllerNavigationContext';

import { EventBus, EVENTS } from '../shared/EventBus';
import { PreGameFlow } from './ui/PreGame/PreGameFlow';
import { isLeavingWorld } from './services/worldExit';
interface GameProps {
  onExit: () => void;
  initialScene?: string;
}
import { useAuth } from './hooks/useAuth';
import { useGameBoot } from './hooks/useGameBoot';
import { useSeamlessTravelScene } from './hooks/useSeamlessTravelScene';
import { LoadingScreen } from './ui/components/LoadingScreen';
import { TravelVeil } from './ui/components/TravelVeil';
import { JoinRefusedScreen } from './ui/components/JoinRefusedScreen';
import { useMultiplayerOtherPlayers } from './diagnostics/useMultiplayerDiagnostics';
import { useBuildVersionCheck } from './hooks/useBuildVersionCheck';
import { onReactCommit } from './diagnostics/reactCommitBudget';
import { withLivePosition } from './systems/player/livePlayerPosition';
import { withLiveVitals } from './systems/player/livePlayerVitals';


export const Game: React.FC<GameProps> = ({ onExit: _onExit, initialScene: propInitialScene }) => {
  const [initialScene] = useState<string>(propInitialScene || 'GameScene');

  // Stale-build drift toast — fires once if the deployed bundle changes
  // SHA after this tab booted. Pairs with ?reset=1 (manual cache wipe).
  useBuildVersionCheck();

  // 1. Auth & Character Management
  const {
    authToken,
    isAuthenticated,
    player,
    setPlayer,
    handleStartGame,
  } = useAuth();

  // 2. Phaser Lifecycle Management
  const {
    gameInstance,
    gameWorld,
    gameContainerRef,
  } = useGameBoot({
    player,
    authToken,
    isAuthenticated,
    initialScene
  });

  // In-place portal trips (plan 25, behind ?seamlessPortals=1) restart the
  // scene on this same host.
  useSeamlessTravelScene(gameInstance, player, authToken);

  const characterService = useRef(CharacterService.getInstance());
  const hasRestartedRef = useRef(false);
  // Loading GameScene is asynchronous. Authoritative snapshots update
  // `player` while that import is in flight; without a latch each update ran
  // this effect again and started another GameScene/kernel. The duplicated
  // kernels then forwarded one inventory click three times over the wire.
  const gameSceneStartRequestedRef = useRef(false);

  // 3. Game Data State
  const [currentLevelName, setCurrentLevelName] = useState<string>('');
  const [currentLevelId, setCurrentLevelId] = useState<string>(DevConfig.defaultMap);

  // Debug / Tools State
  const [showCombatLog, setShowCombatLog] = useState(false);

  // 4. Integrated Services & Hooks
  const {
    isConnected,
    isHotbarHydrated,
    disconnectReason,
    joinRefusal,
    connectToServer,
  } = useServerConnection({
    player,
    gameWorld,
    authToken,
    setPlayer,
  });

  const { showDevActionLogs, setShowDevActionLogs, logViewerVisible: _logViewerVisible, setLogViewerVisible: _setLogViewerVisible } = useDebugConfig();
  const { isEditorMode, setIsEditorMode } = useEditorMode({ gameWorld });

  // XP feedback hook
  useXPFeedback({ player, gameWorld });

  const { handleDevAction } = useDevActions({
    player,
    gameWorld,
    gameInstance,
    authToken,
    setPlayer,
    setShowCombatLog,
    showDevActionLogs,
    setShowDevActionLogs,
    characterService: characterService.current
  });

  useGameEvents({
    player,
    setPlayer,
    gameWorld,
    setCurrentLevelName,
    setCurrentLevelId,
    handleDevAction
  });

  // Sync player state to GameScene
  useEffect(() => {
    if (gameWorld && player && !hasRestartedRef.current) {
      gameWorld.player = player;
      hasRestartedRef.current = true;
      gameLogger.info('game', '🎮 Player synced to GameScene');
    }
  }, [gameWorld, player]);

  // Connect to Server
  useEffect(() => {
    // A refused join stays refused: no retry loop (JoinRefusedScreen below).
    // Leaving from the game menu left the room on purpose: no re-join either.
    if (DevConfig.systems.multiplayer && isAuthenticated && player && !isConnected && !joinRefusal && !isLeavingWorld()) {
      connectToServer();
    }
  }, [isAuthenticated, player, isConnected, joinRefusal, connectToServer]);

  // Load and start GameScene once the server connection is established.
  // This keeps GameScene's heavy v8.evaluateModule out of the pre-room window.
  useEffect(() => {
    if (!gameInstance || !isConnected || gameWorld || !player || gameSceneStartRequestedRef.current) return;
    gameSceneStartRequestedRef.current = true;
    const boot = (window as any).__GAME_BOOT__;
    if (boot) boot.stage = 'Entering world...';

    void import('./scenes/GameScene').then(({ GameScene }) => {
      gameInstance.sceneRegistry.add('GameScene', GameScene);
      const initialLevelId = BootService.getInstance().getInitialLevelId(null);
      const initialSpawnId = BootService.getInstance().getInitialSpawnId();
      const dbChar = characterService.current.convertGameToDBCharacter(player);
      const startData = {
        token: authToken,
        characterId: player._id,
        characterData: dbChar,
        levelId: initialLevelId,
        spawnId: initialSpawnId,
        playerClass: player.class,
      };
      gameInstance.scenes.start('GameScene', startData);
    }).catch((error) => {
      gameSceneStartRequestedRef.current = false;
      gameLogger.error('game', '❌ Failed to load GameScene', { error });
    });
  }, [gameInstance, isConnected, gameWorld, player, authToken]);

  // Sync connection state to UI Context
  const { setIsConnected, otherPlayers, setOtherPlayers } = useGame();
  // The roster only — the full diagnostics state changes every 250 ms (B7).
  const multiplayerOtherPlayers = useMultiplayerOtherPlayers();
  useEffect(() => {
    setIsConnected(isConnected);
  }, [isConnected, setIsConnected]);

  useEffect(() => {
    setOtherPlayers(multiplayerOtherPlayers);
  }, [multiplayerOtherPlayers, setOtherPlayers]);

  // Chat & Action Dispatchers
  const chatProcessor = useMemo(() => new ChatCommandProcessor({
    getPlayer: () => player,
    getGmLevel: () => player?.gmLevel ?? 5, // Default 5 (admin) when no schema sync yet (dev/single-player)
    getGameWorld: () => gameWorld,
    setPlayer,
    handleDevAction,
  }), [player, gameWorld, handleDevAction]);

  useEffect(() => {
    const handleCommand = (data: { command: string }) => {
      chatProcessor.process(data.command);
    };
    EventBus.getInstance().on(EVENTS.CHAT_COMMAND, handleCommand);
    return () => {
      EventBus.getInstance().off(EVENTS.CHAT_COMMAND, handleCommand);
    };
  }, [chatProcessor]);

  const actionDispatcher = useMemo(() => new ActionDispatcher({
    getPlayer: () => player,
    getGameWorld: () => gameWorld,
    setPlayer,
    handleDevAction,
    chatProcessor,
  }), [player, gameWorld, handleDevAction, chatProcessor]);

  const handleAction = (action: string, data?: any) => actionDispatcher.dispatch(action, data);

  // Wire the imperative action bridge so engine-mounted (propless) gameplay
  // windows can dispatch player actions (updateStats / useItem / …) without
  // prop-threading — they live under WindowHost, a sibling of <Game/>. Mirror
  // of how WindowManagerProvider wires windowBridge.toggle.
  useEffect(() => {
    uiActionBridge.dispatch = (action, data) => actionDispatcher.dispatch(action, data);
    // Expose the dev-action channel + overlay readouts on window for the
    // console and automated VQA drivers (dev/localhost/PTR only).
    installHelixDebugConsole();
  }, [actionDispatcher]);


  if (!isAuthenticated || !player) {
    return (
      <PreGameFlow
        onStartGame={handleStartGame}
        initialToken={authToken}
      />
    );
  }

  return (
    // Transparent backdrop: the world renders on #pixi-overlay-host (a
    // document.body child at z-index 5). #root is promoted to its own
    // stacking context ABOVE that canvas (src/client/styles/index.css), so
    // ALL React UI — including debug windows — paints over the terrain. This
    // wrapper must stay non-opaque or it would occlude the canvas now that
    // #root sits above it; the dark backdrop falls back to body's background.
    // Supersedes the partial 2026-05-19 zIndex:100 overlay-only fix below.
    // id="world-passthrough": this wrapper is an inert full-screen layout div
    // above the Pixi canvas — clicks on empty world land HERE (or on #root/
    // body). PixiOverlayHost's world-click forwarder uses the id to know the
    // click hit no real UI and re-dispatches it onto the canvas so sprite
    // click-to-target works (2026-06-11).
    // Profiler: the UI tree's commit time feeds the `react` frame phase
    // (src/game/diagnostics/reactCommitBudget.ts; dev builds only).
    <Profiler id="game" onRender={onReactCommit}>
    <div id="world-passthrough" style={{ position: 'relative', width: '100vw', height: '100vh', overflow: 'hidden', background: 'transparent' }}>
      <div
        ref={gameContainerRef}
        id={DevConfig.game.parent}
        // pointerEvents none (2026-06-11): this legacy mount target renders
        // nothing post-de-Phaser but sat ABOVE the Pixi canvas inside #root's
        // stacking context with default pointer-events — swallowing every
        // world click, so mob/NPC click-to-target never received a
        // pointerdown. All game input (DOMInput, InputSystem recovery,
        // editor) listens on window, so nothing needed clicks on this div.
        style={{ width: '100%', height: '100%', position: 'relative', pointerEvents: 'none' }}
      />

      {joinRefusal ? <JoinRefusedScreen refusal={joinRefusal} /> : <LoadingScreen worldReady={Boolean(gameWorld)} />}
      <TravelVeil />

      {isAuthenticated && (
        // 2026-05-19 — explicit zIndex: 100 on the React UI overlay container.
        // Without it the overlay div lived at stacking level 'auto' (same as
        // the game canvas siblings), and in-canvas debug labels / world-text
        // could paint above some React panels. DJ flagged: "terrain is on a
        // more forward z than most of our UI except chat." Chat worked
        // because its panel sets zIndex:100 directly. Promoting the whole
        // overlay subtree to layer 100 makes the floor consistent.
        <div style={{
          position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', pointerEvents: 'none',
          display: 'flex', flexDirection: 'column', zIndex: 100,
        }}>
          {DevConfig.systems.ui && (
            <ErrorBoundary name="GameUI">
              <ControllerNavigationProvider>
                <GameUIOverlay
                  player={player}
                  otherPlayers={otherPlayers}
                  isConnected={isConnected}
                  isHotbarHydrated={isHotbarHydrated}
                  disconnectReason={disconnectReason}
                  currentLevelName={currentLevelName}
                  currentLevelId={currentLevelId}
                  onAction={handleAction}
                  showDevActionLogs={showDevActionLogs}
                  isEditorMode={isEditorMode}
                  onExitEditor={() => {
                    setIsEditorMode(false);
                    if (gameWorld) {
                      const dbChar = characterService.current.convertGameToDBCharacter(withLiveVitals(withLivePosition(player!)));
                      // Phase H10 Arc λ.9 Stage D-2 (2026-05-10): scene
                      // restart via SceneRegistry through __GAME_HOST__.scenes
                      // (the Phaser scene plugin is gone post-Stage-D-1).
                      const host = (window as any).__GAME_HOST__;
                      host?.scenes?.start('GameScene', {
                        token: authToken,
                        characterId: player!._id,
                        characterData: dbChar,
                        levelId: 'dev-map'
                      });
                    }
                  }}
                />
              </ControllerNavigationProvider>
            </ErrorBoundary>
          )}
        </div>
      )}
      <ErrorToastOverlay />
      {isScreensaverMode() && <ScreensaverOverlay />}
      {isScreensaverMode() && <ScreensaverController />}
      <CombatLogPanel isOpen={showCombatLog} onClose={() => setShowCombatLog(false)} />
    </div>
    </Profiler>
  );
};
